import { LocationVarInheritance } from "@swivl-mono/types";
import { Tooltip } from "@swivl-mono/ui";
import { GrRevert } from "react-icons/gr";

import { CortexInheritancePill } from "./CortexInheritancePill";
import { REVERT_TO_DEFAULT, REVERT_TO_PARENT } from "./revertTarget";

interface RowInheritanceControlsProps {
  inheritance: LocationVarInheritance;
  /** An unsaved edit is staged at this scope. */
  overrideStaged: boolean;
  /** Human-readable value a revert would land on. Omit to render no tooltip. */
  revertsTo?: string;
  /** False on topics gated by an Edit button, where Customize has nothing to add. */
  showCustomize?: boolean;
  /** Overrides the revert wording — e.g. at org scope, where the "parent" is the platform default. */
  revertLabel?: string;
  onCustomize: () => void;
  onRevert: () => void;
  onDiscard: () => void;
}

/** Pill + Customize/Revert action, shared by worksheet rows. */
export function RowInheritanceControls({
  inheritance,
  overrideStaged,
  revertsTo,
  showCustomize = true,
  revertLabel: revertLabelOverride,
  onCustomize,
  onRevert,
  onDiscard
}: RowInheritanceControlsProps): React.ReactElement {
  const isOverride = inheritance === LocationVarInheritance.NONE;
  const customized = isOverride || overrideStaged;

  const revertLabel =
    revertLabelOverride ?? (overrideStaged && inheritance === LocationVarInheritance.DEFAULT ? REVERT_TO_DEFAULT : REVERT_TO_PARENT);

  const handleRevert = (): void => {
    if (!window.confirm(`${revertLabel}? This will discard the current customization for this field.`)) return;
    if (overrideStaged) onDiscard();
    else onRevert();
  };

  return (
    <>
      {/* Show Customized while staged. */}
      <CortexInheritancePill inheritance={customized ? LocationVarInheritance.NONE : inheritance} />
      {customized ? (
        <Tooltip content={revertsTo === undefined ? undefined : `Reverts to: ${revertsTo}`}>
          <button
            type="button"
            onClick={handleRevert}
            className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap text-grey-900 hover:text-orange-500"
          >
            {revertLabel}
            <GrRevert className="size-4" />
          </button>
        </Tooltip>
      ) : showCustomize ? (
        <button
          type="button"
          onClick={onCustomize}
          className="rounded-md border border-orange-300 px-2.5 py-1 text-xs font-semibold text-orange-600 hover:bg-orange-50"
        >
          Customize
        </button>
      ) : null}
    </>
  );
}
